import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Announcement {
  readonly announcementId: string;
  readonly title: string;
  readonly announcementType: string;
  readonly approvalStatus: string;
  readonly isCritical: boolean;
}

// Mirrors backend M27AnnouncementType — server rejects any other value.
const ANN_TYPES = ['CORPORATE', 'POLICY', 'CAMPAIGN', 'SYSTEM', 'TRAINING', 'REGIONAL', 'EMERGENCY'];

/** M27 — Announcements Dashboard (§31). Lists announcements with a status
 * filter; critical items are visually flagged (BR-04). Supports the full
 * DRAFT→SUBMITTED→APPROVED→PUBLISHED lifecycle (create/submit/approve/publish/
 * expire) over announcement.controller (ANNOUNCE_MANAGE). */
export function M27AnnouncementsDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [annType, setAnnType] = useState('');
  const [critical, setCritical] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const q = status ? `?approvalStatus=${encodeURIComponent(status)}` : '';
    try {
      const r = await apiRequest<{ data: Announcement[]; total: number }>(`/v1/collab/announcements${q}`, { token });
      setRows(r.data); setTotal(r.total); setError(null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [token, status]);

  useEffect(() => { void load(); }, [load]);

  async function create(): Promise<void> {
    if (!title.trim() || !body.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest('/v1/collab/announcements', {
        token, method: 'POST',
        body: { title, body, announcementType: annType || undefined, isCritical: critical },
      });
      setTitle(''); setBody(''); setAnnType(''); setCritical(false);
      setNotice(tr('m27.announcements.noticeCreated'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function act(id: string, action: 'submit' | 'approve' | 'publish'): Promise<void> {
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/announcements/${id}/${action}`, { token, method: 'POST' });
      setNotice(tr(action === 'submit' ? 'm27.announcements.noticeSubmitted' : action === 'approve' ? 'm27.announcements.noticeApproved' : 'm27.announcements.noticePublished'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function expire(id: string): Promise<void> {
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/announcements/${id}`, { token, method: 'DELETE' });
      setNotice(tr('m27.announcements.noticeExpired'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="announcements-dashboard">
      <h1>{tr('m27.announcements.heading')}</h1>

      <form aria-label="announcement-create-form" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <label htmlFor="ann-title">{tr('m27.announcements.fldTitle')}</label>
        <input id="ann-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label htmlFor="ann-body">{tr('m27.announcements.fldBody')}</label>
        <textarea id="ann-body" value={body} onChange={(e) => setBody(e.target.value)} required />
        <label htmlFor="ann-type">{tr('m27.announcements.fldType')}</label>
        <select id="ann-type" value={annType} onChange={(e) => setAnnType(e.target.value)}>
          <option value="">{tr('m27.announcements.optDefaultCorporate')}</option>
          {ANN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label htmlFor="ann-critical">
          <input id="ann-critical" type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} /> {tr('m27.announcements.fldCritical')}
        </label>
        <button type="submit" disabled={busy}>{tr('m27.announcements.btnCreate')}</button>
      </form>

      <label htmlFor="ann-status">{tr('m27.announcements.fldStatus')}</label>
      <select id="ann-status" value={status} onChange={(e) => setStatus(e.target.value)}>
        {['', 'DRAFT', 'SUBMITTED', 'APPROVED', 'PUBLISHED', 'EXPIRED'].map((s) => <option key={s || 'all'} value={s}>{s || tr('m27.announcements.optAll')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m27.announcements.loading')}</p> : (
        <>
          <p aria-label="announcement-count">{tr('m27.announcements.count', { count: total })}</p>
          <table aria-label="announcement-table">
            <thead><tr><th>{tr('m27.announcements.fldTitle')}</th><th>{tr('m27.announcements.fldType')}</th><th>{tr('m27.announcements.fldStatus')}</th><th>{tr('m27.announcements.colPriority')}</th><th>{tr('m27.announcements.colActions')}</th></tr></thead>
            <tbody>{rows.map((a) => (
              <tr key={a.announcementId}>
                <td>{a.title}</td><td>{a.announcementType}</td><td>{a.approvalStatus}</td><td>{a.isCritical ? tr('m27.announcements.fldCritical') : tr('m27.announcements.priorityNormal')}</td>
                <td>
                  <button type="button" aria-label={`submit-${a.announcementId}`} disabled={busy} onClick={() => void act(a.announcementId, 'submit')}>{tr('m27.announcements.btnSubmit')}</button>
                  <button type="button" aria-label={`approve-${a.announcementId}`} disabled={busy} onClick={() => void act(a.announcementId, 'approve')}>{tr('m27.announcements.btnApprove')}</button>
                  <button type="button" aria-label={`publish-${a.announcementId}`} disabled={busy} onClick={() => void act(a.announcementId, 'publish')}>{tr('m27.announcements.btnPublish')}</button>
                  <button type="button" aria-label={`expire-${a.announcementId}`} disabled={busy} onClick={() => void expire(a.announcementId)}>{tr('m27.announcements.btnExpire')}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
